import { EcoButton, EcoCard, EcoText } from '@components/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addEcoCartScript, getShopifyScripts, removeEcoCartScript } from '@utils/api/shopify';
import { FontColor } from '@utils/tailwind';
import { View } from 'react-native';

interface Props {
  shopName: string;
}

interface ScriptData {
  message: string;
  fontColor: FontColor;
}

export default function ShopifyScriptCard({ shopName }: Props): JSX.Element {
  const queryClient = useQueryClient();
  const { data: scripts, isLoading: scriptsAreLoading } = useQuery(['scripts', shopName], () => getShopifyScripts(shopName));
  const hasScript = (scripts?.script_tags?.[0]?.src || '').includes('ecocart.js');

  const removeScriptMutation = useMutation(() => removeEcoCartScript(shopName), {
    onSuccess: () => {
      queryClient.invalidateQueries(['scripts', shopName]);
    }
  });

  const addScriptMutation = useMutation(() => addEcoCartScript(shopName), {
    onSuccess: () => {
      queryClient.invalidateQueries(['scripts', shopName]);
    }
  });

  const getInStoreScriptData = (): ScriptData => {
    if (scriptsAreLoading) {
      return {
        message: 'Loading...',
        fontColor: 'subdued'
      };
    }

    return {
      message: hasScript ? 'Enabled' : 'Disabled',
      fontColor: hasScript ? 'accent-primary' : 'danger'
    };
  };

  return (
    <EcoCard
      title="Shopify Scripts"
      topRightComponent={
        <EcoButton
          variant="filled"
          colorScheme={hasScript ? 'danger' : 'default'}
          size="sm"
          onPress={() => (hasScript ? removeScriptMutation.mutate() : addScriptMutation.mutate())}
          isLoading={removeScriptMutation.isLoading || addScriptMutation.isLoading}
        >
          {hasScript ? 'Remove Script' : 'Add Script'}
        </EcoButton>
      }
    >
      <View className="flex-row flex-start">
        <EcoText className="w-[80px]" color="subdued">
          In-Store:
        </EcoText>
        <EcoText fontWeight="semibold" color={getInStoreScriptData().fontColor}>
          {getInStoreScriptData().message}
        </EcoText>
      </View>

      <View className="flex-row flex-start">
        <EcoText className="w-[80px]" color="subdued">
          Checkout:
        </EcoText>
        <EcoText fontWeight="semibold" color="subdued">
          Unknown
        </EcoText>
      </View>
    </EcoCard>
  );
}
